import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, CircularProgress, TextField, Typography } from '@mui/material';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { useTranslation } from 'react-i18next';

import AssistantImage from '@public/images/assistant/myta/assistant.jpg';
import {
  ASSISTANT_ICON_HINT,
  ASSISTANT_ICON_RESET_LABEL,
  ASSISTANT_ICON_UPLOAD_LABEL,
  ASSISTANT_NAME_HINT,
} from '@shared/constants/assistantIdentityCopy';
import { PollyLanguage } from '@shared/interfaces/polly';
import { usePreventGameKeydownListening } from '@hooks';
import { useMytaverseEvent } from '@core/dashboard/providers';
import BrandingsService from '@services/BrandingsService';
import FilesService from '@services/FilesService';

import { useEditorMode } from '../EditorModeContext';
import VoiceSection, { AssistantVoices } from './VoiceSection';
import { useAiScriptsTreeStyles } from './styles';

const AUTOSAVE_DEBOUNCE_MS = 600;

const ALLOWED_ICON_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

/**
 * Tells whether a picked file is an image the hub can render as the icon.
 *
 * Mirrors the branding-texture check: some browsers report an empty MIME type
 * for a drag-and-dropped file, so the extension is accepted as a fallback.
 *
 * @param file - File chosen in the icon input.
 * @returns Whether the file may be uploaded.
 */
const isAllowedIconFile = (file: File): boolean => {
  const mimeOk = !!file.type && ALLOWED_ICON_MIME_TYPES.includes(file.type);
  const nameOk = /\.(png|jpe?g|webp)$/i.test((file.name ?? '').trim());

  return mimeOk || nameOk;
};

interface IAssistantIdentityPatch {
  assistantName?: string | null;
  assistantIcon?: string | null;
  assistantVoices?: AssistantVoices;
}

/**
 * "General" section of the AI panel: the assistant's identity for the
 * configuration currently open in the editor.
 *
 * Both values live on the branding record rather than on the experience, so
 * switching configuration switches the assistant's look with everything else
 * it brands. Leaving them empty is the normal case — the hub then falls back
 * to the room's icon/name (set in the CMS), and finally to its bundled icon
 * and built-in name. See `useAssistantIdentity` for the full resolution order.
 *
 * Note this is deliberately NOT `settings.aiVirtualAssistantName`: that value
 * gates whether the assistant mounts at all and has to keep its exact
 * technical value.
 */
const GeneralTab = () => {
  const { t: translate } = useTranslation();
  const classes = useAiScriptsTreeStyles();
  const { setAutosaveStatus } = useEditorMode();
  const {
    currentBrandingId,
    currentRoom,
    currentBranding,
    currentEvent,
    setCurrentBranding,
  } = useMytaverseEvent();

  // The record the editor has open; falls back to what the room is running so
  // the panel still shows the live values before a configuration is picked.
  const brandingId = currentBrandingId ?? currentRoom?.activeBrandingId ?? null;

  // Read from the event rather than the project record: the backend copies
  // the project's assistant settings onto the event, and this is the same
  // value the assistant resolves voices against at runtime — so the panel
  // shows what will actually be heard.
  //
  // Languages stay the project's. The scripts were authored and translated
  // against them, so a configuration picks voices for those languages rather
  // than choosing a set of its own.
  const projectAi = currentEvent?.settings?.aiAssistant;
  const baseLanguage =
    projectAi?.baseLanguage ?? projectAi?.language ?? PollyLanguage.ENGLISH;
  const projectLanguages = projectAi?.languages ?? [];
  const projectVoices = useMemo<AssistantVoices>(() => {
    const resolved: AssistantVoices = { ...projectAi?.voicesByLanguage };

    // The project keeps the base language's voice in its top-level
    // engine/voiceId as well as in the map, and older projects only have the
    // former.
    if (!resolved[baseLanguage] && projectAi?.engine && projectAi?.voiceId) {
      resolved[baseLanguage] = {
        engine: projectAi.engine,
        voiceId: projectAi.voiceId,
      };
    }

    return resolved;
  }, [projectAi, baseLanguage]);

  const [name, setName] = useState('');
  const [iconUrl, setIconUrl] = useState('');
  const [voices, setVoices] = useState<AssistantVoices>({});
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nameInputRef = usePreventGameKeydownListening<HTMLInputElement>();
  const nameDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The record arrives from the event provider rather than being fetched here,
  // so until it lands the fields would be editing values nobody has read yet —
  // and the effect below would overwrite whatever was typed meanwhile.
  const isLoaded = !!brandingId && currentBranding?.brandingId === brandingId;

  useEffect(() => {
    setName(
      typeof currentBranding?.assistantName === 'string'
        ? currentBranding.assistantName
        : '',
    );
    setIconUrl(
      typeof currentBranding?.assistantIcon === 'string'
        ? currentBranding.assistantIcon
        : '',
    );
    setVoices(currentBranding?.assistantVoices ?? {});
    setError(null);
  }, [currentBranding]);

  useEffect(() => {
    return () => {
      if (nameDebounceRef.current) {
        clearTimeout(nameDebounceRef.current);
      }
    };
  }, []);

  /**
   * Writes one identity field and mirrors what was stored onto the shared
   * branding record.
   *
   * The mirror is what makes the change visible immediately: every consumer of
   * the assistant's identity reads the record from the event provider, so
   * without it the panel would show the new value while the button and the
   * avatar kept the old one until the next refetch.
   *
   * What gets mirrored is the response, not the request. An uploaded icon is
   * moved out of the temporary `uploads/` prefix as the backend persists it —
   * the URL sent here stops resolving the moment that copy-and-delete lands,
   * so only the value that comes back is safe to keep.
   *
   * @param patch - Fields to write; an empty string clears one.
   * @returns What was stored, or `null` when the write did not happen.
   */
  const persist = useCallback(
    async (
      patch: IAssistantIdentityPatch,
    ): Promise<IAssistantIdentityPatch | null> => {
      if (!brandingId) {
        return null;
      }

      setAutosaveStatus('saving');
      try {
        const updated = await BrandingsService.updateBranding(
          brandingId,
          patch,
        );

        const stored: IAssistantIdentityPatch = { ...patch };

        if (patch.assistantIcon !== undefined) {
          const value = updated?.assistantIcon;
          if (typeof value === 'string' || value === null) {
            stored.assistantIcon = value;
          }
        }

        if (patch.assistantName !== undefined) {
          const value = updated?.assistantName;
          if (typeof value === 'string' || value === null) {
            stored.assistantName = value;
          }
        }

        setCurrentBranding((previous) =>
          previous && previous.brandingId === brandingId
            ? { ...previous, ...stored }
            : previous,
        );
        setAutosaveStatus('saved');

        return stored;
      } catch (saveError) {
        console.error(
          `Failed to save assistant identity: ${(saveError as Error)?.message}`,
          { brandingId },
        );
        setAutosaveStatus('error');

        return null;
      }
    },
    [brandingId, setAutosaveStatus, setCurrentBranding],
  );

  /**
   * Stages a typed name and schedules the debounced save.
   *
   * @param value - Raw field value; saved trimmed, and an empty one clears the
   *   override.
   */
  const handleNameChange = (value: string) => {
    setName(value);

    if (nameDebounceRef.current) {
      clearTimeout(nameDebounceRef.current);
    }
    nameDebounceRef.current = setTimeout(() => {
      persist({ assistantName: value.trim() });
    }, AUTOSAVE_DEBOUNCE_MS);
  };

  /**
   * Uploads the picked image and shows the icon under the URL it ends up on.
   *
   * The uploaded URL is only good enough for the optimistic preview: the
   * backend relocates the file as it saves, so the permanent URL is whatever
   * `persist` reports back.
   *
   * @param event - Change event from the hidden file input.
   */
  const handleFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    // Cleared so picking the same file again still fires a change event.
    event.target.value = '';

    if (!file) {
      return;
    }

    if (!isAllowedIconFile(file)) {
      setError(
        translate(
          'editorMode.aiGeneralIconUnsupported',
          'Use a PNG, JPEG or WebP image.',
        ),
      );
      return;
    }

    setError(null);
    setIsUploading(true);

    try {
      const [uploadInfo] = await FilesService.uploadFilesWithProgress([file]);

      setIconUrl(uploadInfo.url);

      const stored = await persist({ assistantIcon: uploadInfo.url });
      if (typeof stored?.assistantIcon === 'string') {
        setIconUrl(stored.assistantIcon);
      }
    } catch (uploadFailure) {
      console.error(
        `Failed to upload assistant icon: ${(uploadFailure as Error)?.message}`,
        { brandingId },
      );
      setError(
        translate(
          'editorMode.aiGeneralIconUploadFailed',
          'Upload failed. Try again.',
        ),
      );
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * Drops the branding's icon so the hub falls back to the room's icon, or
   * its bundled artwork if the room has none either.
   */
  const handleResetIcon = async () => {
    setError(null);
    setIconUrl('');
    await persist({ assistantIcon: '' });
  };

  /**
   * Stores the configuration's complete voice map.
   *
   * Written whole rather than per language: the backend replaces the attribute
   * on every write, so a language dropped from the map is a language handed
   * back to the project.
   *
   * @param nextVoices - Overrides to keep, keyed by language.
   */
  const handleVoicesChange = (nextVoices: AssistantVoices) => {
    setVoices(nextVoices);
    persist({ assistantVoices: nextVoices });
  };

  const disabled = !isLoaded;

  return (
    <Box sx={classes.generalPanel} data-testid="ai-tree-general-panel">
      <Box sx={classes.generalField}>
        <Typography sx={classes.generalLabel}>
          {translate('editorMode.aiGeneralIcon', 'Assistant icon')}
        </Typography>
        <Box sx={classes.generalIconRow}>
          <Box
            component="img"
            src={iconUrl || AssistantImage}
            alt={translate('editorMode.aiGeneralIcon', 'Assistant icon')}
            sx={classes.generalIconPreview}
            data-testid="ai-tree-general-icon-preview"
          />
          <Box sx={classes.generalIconActions}>
            <Box
              component="label"
              sx={[
                classes.generalButton,
                (disabled || isUploading) && classes.generalButtonDisabled,
              ]}
              data-testid="ai-tree-general-icon-upload-btn"
            >
              {isUploading ? (
                <CircularProgress size={13} sx={classes.generalSpinner} />
              ) : (
                <UploadFileOutlinedIcon sx={{ fontSize: 14 }} />
              )}
              {translate(
                'editorMode.aiGeneralIconUpload',
                ASSISTANT_ICON_UPLOAD_LABEL,
              )}
              <input
                hidden
                type="file"
                accept="image/png,image/jpeg,image/webp"
                disabled={disabled || isUploading}
                onChange={handleFileSelected}
                data-testid="ai-tree-general-icon-upload-input"
              />
            </Box>
            {iconUrl ? (
              <Box
                component="button"
                type="button"
                sx={[
                  classes.generalButton,
                  (disabled || isUploading) && classes.generalButtonDisabled,
                ]}
                disabled={disabled || isUploading}
                onClick={handleResetIcon}
                data-testid="ai-tree-general-icon-reset-btn"
              >
                {translate(
                  'editorMode.aiGeneralIconReset',
                  ASSISTANT_ICON_RESET_LABEL,
                )}
              </Box>
            ) : null}
          </Box>
        </Box>
        <Typography sx={classes.generalHelper}>
          {translate('editorMode.aiGeneralIconHint', ASSISTANT_ICON_HINT)}
        </Typography>
      </Box>

      <Box sx={classes.generalField}>
        <Typography sx={classes.generalLabel}>
          {translate('editorMode.aiGeneralName', 'Assistant name')}
        </Typography>
        <TextField
          inputRef={nameInputRef}
          size="small"
          fullWidth
          value={name}
          disabled={disabled}
          placeholder={translate('assistant.noa')}
          onChange={(changeEvent) => handleNameChange(changeEvent.target.value)}
          onKeyDown={(keyEvent) => {
            if (keyEvent.key !== 'Escape') {
              keyEvent.stopPropagation();
            }
          }}
          onKeyUp={(keyEvent) => {
            if (keyEvent.key !== 'Escape') {
              keyEvent.stopPropagation();
            }
          }}
          sx={classes.generalTextField}
          inputProps={{ maxLength: 40, 'data-testid': 'ai-tree-general-name' }}
        />
        <Typography sx={classes.generalHelper}>
          {translate('editorMode.aiGeneralNameHint', ASSISTANT_NAME_HINT)}
        </Typography>
      </Box>

      <VoiceSection
        baseLanguage={baseLanguage}
        languages={projectLanguages}
        projectVoices={projectVoices}
        voices={voices}
        assistantName={name.trim()}
        disabled={disabled}
        onChange={handleVoicesChange}
      />

      {error ? (
        <Typography sx={classes.generalError}>{error}</Typography>
      ) : null}

      {!brandingId ? (
        <Typography sx={classes.generalHelper}>
          {translate(
            'editorMode.aiGeneralNoBranding',
            'Pick a configuration to edit its assistant.',
          )}
        </Typography>
      ) : null}
    </Box>
  );
};

export default GeneralTab;
